import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  styled,
  Switch,
  Select,
  MenuItem,
  IconButton,
  SelectChangeEvent,
  InputLabel,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { currencyDB } from '../../lib/currency-list';
import { LeadFormData } from '../../types/leads';

const Container = styled(Box)`
  max-width: 600px;
  margin: 0 auto;
`;

const Section = styled(Box)`
  margin-bottom: 32px;
  padding: 16px;
  background-color: ${props => props.theme.palette.background.paper};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled(Typography)`
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.theme.palette.text.secondary};
  margin-bottom: 16px;
`;

const Field = styled(Box)`
  margin: 12px 0;
`;

const CounterField = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const SubmitButton = styled(Button)`
  width: 100%;
  padding: 12px;
  margin-top: 32px;
  background-color: #00A699;
  &:hover {
    background-color: #008F86;
  }
`;

const SwitchField = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

interface TripQuestionnaireProps {
  onSubmit: (data: LeadFormData) => void;
}

export default function TripQuestionnaire({ onSubmit }: TripQuestionnaireProps) {
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phoneNumber: '',
    tripName: '',
    tripType: '',
    numAdults: 1,
    numChildren: 0,
    numInfants: 0,
    tripLocation: '',
    locationPrecision: '',
    originLocation: '',
    timingPrecision: '',
    startDate: '',
    endDate: '',
    includeTransport: false,
    transportType: '',
    pricingType: '',
    preferredCarrier: '',
    includeAccommodation: false,
    accommodationType: '',
    preferredBrand: '',
    includeMeals: false,
    numBreakfastVenue: 0,
    numLunchVenue: 0,
    numDinnerVenue: 0,
    numActivities: 0,
    breakfastVenueType: '',
    breakfastPricingType: '',
    lunchVenueType: '',
    lunchPricingType: '',
    dinnerVenueType: '',
    dinnerPricingType: '',
    activities: [],
    carSize: '',
    preferredRentalType: '',
    pricingPreference: '',
    includeActivities: false,
    budgetCurrency: '',
    minBudget: '',
    maxBudget: '',
    comments: '',
  });

  const handleTextChange = (field: keyof LeadFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleActivities = (index: number) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => {
      const newActivities = [...(prev.activities ?? [])];
      newActivities[index] = event.target.value;

      return { ...prev, activities: newActivities };
    });
  };

  const handleSelectChange = (field: keyof LeadFormData) => (
    event: SelectChangeEvent<string>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSwitchChange = (field: keyof LeadFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleCounter = (field: 'numAdults' | 'numChildren' | 'numInfants' | 'numBreakfastVenue' | 'numLunchVenue' | 'numDinnerVenue' | 'numActivities', increment: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: increment ? prev[field] + 1 : Math.max(0, prev[field] - 1),
    }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Container>
      <Typography variant="h4" textAlign="center" fontWeight={600} gutterBottom>What are you planning?</Typography>
      <Typography textAlign="center" my={3}>Background information on Trip you are planning.</Typography>
      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Trip</SectionTitle>
        <Field>
          <TextField
            fullWidth
            label="Trip Name"
            value={formData.tripName}
            onChange={handleTextChange('tripName')}
          />
        </Field>
        <FormControl fullWidth>
          <InputLabel id="trip-type-select-label">Trip Type</InputLabel>
          <Select
            id="trip-type-select-label"
            labelId="trip-type-select-label"
            label="Trip Type"
            fullWidth
            value={formData.tripType}
            onChange={handleSelectChange('tripType')}
            displayEmpty
            placeholder="Trip Type"
          >
            <MenuItem value="Open to Anything">Open to Anything</MenuItem>
            <MenuItem value="Learn & Explore">Learn & Explore</MenuItem>
            <MenuItem value="Relax & Recharge">Relax & Recharge</MenuItem>
            <MenuItem value="Escape & Reflect">Escape & Reflect</MenuItem>
            <MenuItem value="Have an Adventure">Have an Adventure</MenuItem>
            <MenuItem value="Celebration Event">Celebration Event</MenuItem>
            <MenuItem value="Socialise & Experience">Socialise & Experience</MenuItem>
            <MenuItem value="Work">Work</MenuItem>
            <MenuItem value="Visit Someone">Visit Someone</MenuItem>
            <MenuItem value="Family Getaway">Family Getaway</MenuItem>
          </Select>
        </FormControl>
      </Section>
      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Who</SectionTitle>
        <CounterField>
          <Typography>Number of Adults</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => handleCounter('numAdults', false)} size="small">
              <RemoveIcon />
            </IconButton>
            <Typography>{formData.numAdults}</Typography>
            <IconButton onClick={() => handleCounter('numAdults', true)} size="small">
              <AddIcon />
            </IconButton>
          </Box>
        </CounterField>
        <CounterField>
          <Typography>Number of Children (2-11)</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => handleCounter('numChildren', false)} size="small">
              <RemoveIcon />
            </IconButton>
            <Typography>{formData.numChildren}</Typography>
            <IconButton onClick={() => handleCounter('numChildren', true)} size="small">
              <AddIcon />
            </IconButton>
          </Box>
        </CounterField>
        <CounterField>
          <Typography>Number of Infants</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => handleCounter('numInfants', false)} size="small">
              <RemoveIcon />
            </IconButton>
            <Typography>{formData.numInfants}</Typography>
            <IconButton onClick={() => handleCounter('numInfants', true)} size="small">
              <AddIcon />
            </IconButton>
          </Box>
        </CounterField>
      </Section>

      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Where</SectionTitle>
        <Field>
          <TextField
            fullWidth
            label="Trip Location"
            value={formData.tripLocation}
            onChange={handleTextChange('tripLocation')}
          />
        </Field>
        <FormControl fullWidth>
          <InputLabel id="location-precision">Precision</InputLabel>
          <Select
            labelId='location-precision'
            label="Precision"
            fullWidth
            value={formData.locationPrecision}
            onChange={handleSelectChange('locationPrecision')}
            displayEmpty
          >
            <MenuItem value="Around Continent">Around Continent</MenuItem>
            <MenuItem value="Around Country">Around Country</MenuItem>
            <MenuItem value="Around Place/City/State/Town">Around Place/City/State/Town</MenuItem>
          </Select>
        </FormControl>
        <Field>
          <TextField
            fullWidth
            label="Origin Location"
            value={formData.originLocation}
            onChange={handleTextChange('originLocation')}
          />
        </Field>
      </Section>

      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>When</SectionTitle>
        <FormControl fullWidth>
          <InputLabel id="timing-precision">Timing Precision</InputLabel>
          <Select
            labelId='timing-precision'
            label="Timing Precision"
            fullWidth
            value={formData.timingPrecision}
            onChange={handleSelectChange('timingPrecision')}
            displayEmpty
          >
            <MenuItem value="Exact Dates">Exact Dates</MenuItem>
            <MenuItem value="Few Days either side">Few Days either side</MenuItem>
            <MenuItem value="Totally Flexible">Totally Flexible</MenuItem>
          </Select>
        </FormControl>
        <Field>
          <TextField
            fullWidth
            type="date"
            label="Target Start Date"
            InputLabelProps={{ shrink: true }}
            value={formData.startDate}
            onChange={handleTextChange('startDate')}
          />
        </Field>
        <Field>
          <TextField
            fullWidth
            type="date"
            label="Target End Date"
            InputLabelProps={{ shrink: true }}
            value={formData.endDate}
            onChange={handleTextChange('endDate')}
          />
        </Field>
      </Section>

      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Research Inclusions</SectionTitle>
        <Box>
          <SwitchField>
            <Typography>Include Transport</Typography>
            <Switch
              checked={formData.includeTransport}
              onChange={handleSwitchChange('includeTransport')}
            />
          </SwitchField>
          {formData.includeTransport && (
            <>
              <Field>
                <Select
                  fullWidth
                  value={formData.transportType}
                  onChange={handleSelectChange('transportType')}
                  displayEmpty
                >
                  <MenuItem value="Main Only">Main Only (Flights, Ferries, Long Bus & Train etc.)</MenuItem>
                  <MenuItem value="All">All (Include short transfers, taxis, short trains and bus)</MenuItem>
                </Select>
              </Field>
              <FormControl fullWidth>
                <InputLabel id="type-included">Type Included</InputLabel>
                <Select
                  labelId='type-included'
                  label="Type Included"
                  fullWidth
                  value={formData.pricingType}
                  onChange={handleSelectChange('pricingType')}
                  displayEmpty
                >
                  <MenuItem value="Economy">Economy</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="First Class">First Class</MenuItem>
                </Select>
              </FormControl>
              <Field>
                <TextField
                  fullWidth
                  label="Preferred Carrier"
                  value={formData.preferredCarrier}
                  onChange={handleTextChange('preferredCarrier')}
                />
              </Field>
            </>
          )}
        </Box>
        <Box>
          <SwitchField>
            <Typography>Include Accommodation</Typography>
            <Switch
              checked={formData.includeAccommodation}
              onChange={handleSwitchChange('includeAccommodation')}
            />
          </SwitchField>
          {formData.includeAccommodation && (
            <>
              <FormControl fullWidth>
                <InputLabel id="accommodation-type">Accommodation Type</InputLabel>
                <Select
                  labelId="accommodation-type"
                  label="Accommodation Type"
                  fullWidth
                  value={formData.accommodationType}
                  onChange={handleSelectChange('accommodationType')}
                  displayEmpty
                >
                  <MenuItem value="Budget (No Breakfast)">Budget (No Breakfast)</MenuItem>
                  <MenuItem value="Budget (Include Breakfast)">Budget (Include Breakfast)</MenuItem>
                  <MenuItem value="Good Value (No Breakfast)">Good Value (No Breakfast)</MenuItem>
                  <MenuItem value="Good Value (Include Breakfast)">Good Value (Include Breakfast)</MenuItem>
                  <MenuItem value="Premium (No Breakfast)">Premium (No Breakfast)</MenuItem>
                  <MenuItem value="Premium (Include Breakfast)">Premium (Include Breakfast)</MenuItem>
                  <MenuItem value="Luxury (No Breakfast)">Luxury (No Breakfast)</MenuItem>
                  <MenuItem value="Luxury (Include Breakfast)">Luxury (Include Breakfast)</MenuItem>
                </Select>
              </FormControl>
              <Field>
                <TextField
                  fullWidth
                  label="Preferred Brand"
                  value={formData.preferredBrand}
                  onChange={handleTextChange('preferredBrand')}
                />
              </Field>
            </>
          )}
        </Box>
        <Box>
          <SwitchField>
            <Typography>Include Meals</Typography>
            <Switch
              checked={formData.includeMeals}
              onChange={handleSwitchChange('includeMeals')}
            />
          </SwitchField>
          {formData.includeMeals && (
            <>
              <CounterField>
                <Typography>Breakfast Venue(s)</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={() => handleCounter('numBreakfastVenue', false)} size="small">
                    <RemoveIcon />
                  </IconButton>
                  <Typography>{formData.numBreakfastVenue}</Typography>
                  <IconButton onClick={() => handleCounter('numBreakfastVenue', true)} size="small">
                    <AddIcon />
                  </IconButton>
                </Box>
              </CounterField>
              {formData.numBreakfastVenue > 0 && (
                <>
                  <Field>
                    <FormControl fullWidth>
                      <InputLabel id="breakfast-venue-type">Preferred Venue Type</InputLabel>
                      <Select
                        labelId="breakfast-venue-type"
                        label="Preferred Venue Type"
                        fullWidth
                        value={formData.breakfastVenueType}
                        onChange={handleSelectChange('breakfastVenueType')}
                        displayEmpty
                      >
                        <MenuItem value="any">Any</MenuItem>
                        <MenuItem value="Cheap & Basic">Cheap & Basic</MenuItem>
                        <MenuItem value="Pub & Cafe">Pub & Cafe</MenuItem>
                        <MenuItem value="Trendy">Trendy</MenuItem>
                        <MenuItem value="Fine Dining">Fine Dining</MenuItem>
                        <MenuItem value="Suit Large Group">Suit Large Group</MenuItem>
                        <MenuItem value="Accommodation venue package (if available)">Accommodation venue package (if available)</MenuItem>
                        <MenuItem value="Specific Requirement (Message Me)">Specific Requirement (Message Me)</MenuItem>
                      </Select>
                    </FormControl>
                  </Field>
                  <Field>
                    <FormControl fullWidth>
                      <InputLabel id="breakfast-pricing-type">Pricing Type</InputLabel>
                      <Select
                        labelId="breakfast-pricing-type"
                        label="Preferred Pricing Type"
                        fullWidth
                        value={formData.breakfastPricingType}
                        onChange={handleSelectChange('breakfastPricingType')}
                        displayEmpty
                      >
                        <MenuItem value="Budget">Budget</MenuItem>
                        <MenuItem value="Good Value">Good Value</MenuItem>
                        <MenuItem value="Premium">Premium</MenuItem>
                        <MenuItem value="Luxury">Luxury</MenuItem>
                      </Select>
                    </FormControl>
                  </Field>
                </>
              )}
              <CounterField>
                <Typography>Lunch Venue(s)</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={() => handleCounter('numLunchVenue', false)} size="small">
                    <RemoveIcon />
                  </IconButton>
                  <Typography>{formData.numLunchVenue}</Typography>
                  <IconButton onClick={() => handleCounter('numLunchVenue', true)} size="small">
                    <AddIcon />
                  </IconButton>
                </Box>
              </CounterField>
              {formData.numLunchVenue > 0 && (
                <>
                  <Field>
                    <FormControl fullWidth>
                      <InputLabel id="lunch-venue-type">Preferred Venue Type</InputLabel>
                      <Select
                        labelId="lunch-venue-type"
                        label="Preferred Venue Type"
                        fullWidth
                        value={formData.lunchVenueType}
                        onChange={handleSelectChange('lunchVenueType')}
                        displayEmpty
                      >
                        <MenuItem value="any">Any</MenuItem>
                        <MenuItem value="Cheap & Basic">Cheap & Basic</MenuItem>
                        <MenuItem value="Pub & Cafe">Pub & Cafe</MenuItem>
                        <MenuItem value="Trendy">Trendy</MenuItem>
                        <MenuItem value="Fine Dining">Fine Dining</MenuItem>
                        <MenuItem value="Suit Large Group">Suit Large Group</MenuItem>
                        <MenuItem value="Accommodation venue package (if available)">Accommodation venue package (if available)</MenuItem>
                        <MenuItem value="Specific Requirement (Message Me)">Specific Requirement (Message Me)</MenuItem>
                      </Select>
                    </FormControl>
                  </Field>
                  <Field>
                    <FormControl fullWidth>
                      <InputLabel id="lunch-pricing-type">Pricing Type</InputLabel>
                      <Select
                        labelId="lunch-pricing-type"
                        label="Preferred Pricing Type"
                        fullWidth
                        value={formData.lunchPricingType}
                        onChange={handleSelectChange('lunchPricingType')}
                        displayEmpty
                      >
                        <MenuItem value="Budget">Budget</MenuItem>
                        <MenuItem value="Good Value">Good Value</MenuItem>
                        <MenuItem value="Premium">Premium</MenuItem>
                        <MenuItem value="Luxury">Luxury</MenuItem>
                      </Select>
                    </FormControl>
                  </Field>
                </>
              )}
              <CounterField>
                <Typography>Dinner Venue(s)</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={() => handleCounter('numDinnerVenue', false)} size="small">
                    <RemoveIcon />
                  </IconButton>
                  <Typography>{formData.numDinnerVenue}</Typography>
                  <IconButton onClick={() => handleCounter('numDinnerVenue', true)} size="small">
                    <AddIcon />
                  </IconButton>
                </Box>
              </CounterField>
              {formData.numDinnerVenue > 0 && (
                <>
                  <Field>
                    <FormControl fullWidth>
                      <InputLabel id="dinner-venue-type">Preferred Venue Type</InputLabel>
                      <Select
                        labelId="dinner-venue-type"
                        label="Preferred Venue Type"
                        fullWidth
                        value={formData.dinnerVenueType}
                        onChange={handleSelectChange('dinnerVenueType')}
                        displayEmpty
                      >
                        <MenuItem value="Any">Any</MenuItem>
                        <MenuItem value="Cheap & Basic">Cheap & Basic</MenuItem>
                        <MenuItem value="Pub & Cafe">Pub & Cafe</MenuItem>
                        <MenuItem value="Trendy">Trendy</MenuItem>
                        <MenuItem value="Fine Dining">Fine Dining</MenuItem>
                        <MenuItem value="Suit Large Group">Suit Large Group</MenuItem>
                        <MenuItem value="Accommodation venue package (if available)">Accommodation venue package (if available)</MenuItem>
                        <MenuItem value="Specific Requirement (Message Me)">Specific Requirement (Message Me)</MenuItem>
                      </Select>
                    </FormControl>
                  </Field>
                  <Field>
                    <FormControl fullWidth>
                      <InputLabel id="dinner-pricing-type">Pricing Type</InputLabel>
                      <Select
                        labelId="dinner-pricing-type"
                        label="Preferred Pricing Type"
                        fullWidth
                        value={formData.dinnerPricingType}
                        onChange={handleSelectChange('dinnerPricingType')}
                        displayEmpty
                      >
                        <MenuItem value="Budget">Budget</MenuItem>
                        <MenuItem value="Good Value">Good Value</MenuItem>
                        <MenuItem value="Premium">Premium</MenuItem>
                        <MenuItem value="Luxury">Luxury</MenuItem>
                      </Select>
                    </FormControl>
                  </Field>
                </>
              )}
            </>
          )}
        </Box>
        <Box>
          <SwitchField>
            <Typography>Include Activities</Typography>
            <Switch
              checked={formData.includeActivities}
              onChange={handleSwitchChange('includeActivities')}
            />
          </SwitchField>
          {formData.includeActivities && (
            <>
              <CounterField>
                <Typography>Research Activities</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={() => handleCounter('numActivities', false)} size="small">
                    <RemoveIcon />
                  </IconButton>
                  <Typography>{formData.numActivities}</Typography>
                  <IconButton onClick={() => handleCounter('numActivities', true)} size="small">
                    <AddIcon />
                  </IconButton>
                </Box>
              </CounterField>
              {[...Array(formData.numActivities)].map((_, index) => (
                <Box display="flex" alignItems="center" gap={2} my={1}>
                  <InputLabel>{index + 1}.</InputLabel>
                  <TextField value={formData.activities[index]} fullWidth onChange={handleActivities(index)} />
                </Box>
              ))}
            </>
          )}
        </Box>
      </Section>

      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Trip Budget</SectionTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <FormControl sx={{ width: 300 }}>
            <InputLabel id="budget-currency">Currency</InputLabel>
            <Select
              label="Currency"
              labelId="budget-currency"
              value={formData.budgetCurrency}
              onChange={handleSelectChange('budgetCurrency')}
              displayEmpty
            >
              {currencyDB.map((i) => (
                <MenuItem value={i.value}><span className={`fi fi-${i.code}`} />{i.sign}{i.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Field>
            <TextField
              label="Min Budget"
              type="number"
              value={formData.minBudget}
              onChange={handleTextChange('minBudget')}
            // InputProps={{
            //   startAdornment: <Typography mr={2}>{formData.budgetCurrency}</Typography>,
            // }}
            />
          </Field>
          <Field>
            <TextField
              label="Max Budget"
              type="number"
              value={formData.maxBudget}
              onChange={handleTextChange('maxBudget')}
            // InputProps={{
            //   startAdornment: <Typography mr={2}>{formData.budgetCurrency}</Typography>,
            // }}
            />
          </Field>
        </Box>
      </Section>

      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Guidance</SectionTitle>
        <Field>
          <TextField
            label="Need to know"
            fullWidth
            multiline
            rows={4}
            placeholder="Enter any details or instruction that are important to know about the Trip or participants when researching and creating the itinerary. Eg. Specific requirements, limitations, what will make the overall trip awesome!"
            value={formData.comments}
            onChange={handleTextChange('comments')}
          />
        </Field>
      </Section>

      <Section>
        <SectionTitle sx={{ fontSize: 18, fontWeight: 600 }}>Contact</SectionTitle>
        <Field>
          <TextField label="Name" fullWidth value={formData.name} onChange={handleTextChange('name')} />
        </Field>
        <Field>
          <TextField label="Email" fullWidth value={formData.email} onChange={handleTextChange('email')} />
        </Field>
        <Field>
          <TextField label="Phone Number" fullWidth value={formData.phoneNumber} onChange={handleTextChange('phoneNumber')} />
        </Field>
      </Section>

      <SubmitButton
        variant="contained"
        color="primary"
        onClick={handleSubmit}
      >
        Share Trip Info
      </SubmitButton>
    </Container>
  );
}
